import React, { useEffect } from "react";
import { alpha } from "@mui/material/styles";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import {
  ArrowForward,
  CheckCircleOutline,
  LockOutlined,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import colors from "../../design-system/colors";
import { getLicenseFeature } from "./licenseFeatureCatalog";
import { useLicenseUpgrade } from "./LicenseUpgradeDialog";

const rememberReturnPosition = (pathname) => {
  try {
    sessionStorage.setItem(
      "licenseUpgradeReturn",
      JSON.stringify({ pathname, scrollY: window.scrollY })
    );
  } catch {
    // Navigation remains available when browser storage is unavailable.
  }
};

export function UpgradeCard({ permission, compact = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { requestUpgrade } = useLicenseUpgrade();
  const { permissionError, loading } = useAuth();
  const feature = getLicenseFeature(permission);

  const openPlans = () => {
    if (requestUpgrade) {
      requestUpgrade(permission);
      return;
    }
    const returnTo = `${location.pathname}${location.search}`;
    rememberReturnPosition(returnTo);
    navigate(
      `/licencias?required=${encodeURIComponent(permission)}&returnTo=${encodeURIComponent(returnTo)}`
    );
  };

  if (permissionError) return null;
  if (loading) return <Skeleton variant="rounded" height={120} />;
  return (
    <Card
      role="region"
      aria-label={`${feature.title}: requiere licencia`}
      sx={{
        width: "100%",
        maxWidth: compact ? 680 : 860,
        border: `1px solid ${colors.secondary[400]}`,
        borderRadius: 3,
        boxShadow: "0 18px 50px rgba(15, 23, 42, 0.18)",
        bgcolor: 'background.paper',
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, md: compact ? 3 : 4 } }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                bgcolor: colors.secondary[100],
                color: colors.secondary[800],
                flexShrink: 0,
              }}
            >
              <LockOutlined aria-hidden="true" />
            </Box>
            <Box>
              <Typography variant={compact ? "h6" : "h5"} fontWeight={800}>
                {feature.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {feature.description}
              </Typography>
            </Box>
          </Stack>

          {!compact && feature.benefits.length > 0 && (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} flexWrap="wrap">
              {feature.benefits.map((benefit) => (
                <Stack key={benefit} direction="row" spacing={0.75} alignItems="center">
                  <CheckCircleOutline color="success" sx={{ fontSize: 18 }} />
                  <Typography variant="body2">{benefit}</Typography>
                </Stack>
              ))}
            </Stack>
          )}

          {feature.plans.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
                Desde:
              </Typography>
              {feature.plans.map((plan) => (
                <Chip key={plan} size="small" label={plan} variant="outlined" />
              ))}
            </Stack>
          )}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              variant="contained"
              endIcon={<ArrowForward />}
              onClick={openPlans}
              sx={{ minHeight: 44 }}
            >
              Ver planes
            </Button>
            <Button
              variant="outlined"
              onClick={openPlans}
              sx={{ minHeight: 44 }}
            >
              Comparar licencias
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

export function LockedFeaturePage() {
  return (
    <Box
      component="main"
      aria-hidden="true"
      sx={{
        minHeight: "70vh",
        px: { xs: 2, md: 4 },
        py: { xs: 4, md: 7 },
        pointerEvents: "none",
        filter: "blur(7px)",
        opacity: 0.58,
        background: "background.default",
      }}
    >
      <Stack spacing={3} maxWidth={1180} mx="auto">
        <Skeleton variant="rounded" height={90} />
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          {[1, 2, 3].map((item) => (
            <Skeleton
              key={item}
              variant="rounded"
              height={180}
              sx={{ flex: 1 }}
            />
          ))}
        </Stack>
        <Skeleton variant="rounded" height={280} />
      </Stack>
    </Box>
  );
}

export function LicenseFeatureRoute({ permission, children }) {
  const { hasPermission, loading, permissionError } = useAuth();
  const { requestUpgrade } = useLicenseUpgrade();
  const allowed = hasPermission(permission);

  useEffect(() => {
    if (!loading && !permissionError && !allowed && requestUpgrade) {
      requestUpgrade(permission, { leaveOnClose: true });
    }
  }, [allowed, loading, permissionError, permission, requestUpgrade]);

  if (loading) return <LockedFeaturePage />;
  return allowed
    ? children
    : <LockedFeaturePage />;
}

export function UpgradeBoundary({
  permission,
  promptOnMount = false,
  leaveOnClose = false,
}) {
  const { requestUpgrade } = useLicenseUpgrade();

  useEffect(() => {
    if (promptOnMount && requestUpgrade) {
      requestUpgrade(permission, { leaveOnClose });
    }
  }, [leaveOnClose, permission, promptOnMount, requestUpgrade]);

  if (promptOnMount) {
    return (
      <Box
        aria-hidden="true"
        sx={{
          mt: 1,
          p: 3,
          minHeight: { xs: 260, md: 360 },
          borderRadius: 3,
          pointerEvents: "none",
          filter: "blur(7px)",
          opacity: 0.52,
          bgcolor: "background.paper",
        }}
      >
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={72} />
          <Skeleton variant="rounded" height={110} />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            {[1, 2, 3].map((item) => (
              <Skeleton
                key={item}
                variant="rounded"
                height={100}
                sx={{ flex: 1 }}
              />
            ))}
          </Stack>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: { xs: 360, md: 420 },
        mt: 1,
        overflow: "hidden",
        borderRadius: 3,
        background: (theme) => `linear-gradient(180deg, ${theme.palette.action.hover} 0%, ${theme.palette.action.selected} 100%)`,
      }}
    >
      <Box aria-hidden="true" sx={{ p: 3, opacity: 0.45 }}>
        <Stack spacing={2}>
          {[1, 2, 3].map((item) => (
            <Box
              key={item}
              sx={{
                height: item === 1 ? 90 : 64,
                borderRadius: 2,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
              }}
            />
          ))}
        </Stack>
      </Box>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "grid",
          placeItems: "center",
          px: 2,
          background: (theme) => (theme.palette.mode === 'dark'
            ? `linear-gradient(180deg, ${alpha(theme.palette.background.default, 0.18)} 0%, ${alpha(theme.palette.background.default, 0.96)} 42%)`
            : "linear-gradient(180deg, rgba(245,247,250,0.18) 0%, rgba(245,247,250,0.96) 42%)"),
        }}
      >
        <UpgradeCard permission={permission} />
      </Box>
    </Box>
  );
}

export function LicensePreviewNotice({ permission, visibleCount = 5 }) {
  const { requestUpgrade } = useLicenseUpgrade();
  const { permissionError, loading } = useAuth();
  const feature = getLicenseFeature(permission);

  if (permissionError || loading) return null;
  return (
    <Alert
      severity="info"
      icon={<LockOutlined />}
      action={
        <Button
          color="inherit"
          onClick={() => requestUpgrade?.(permission)}
          sx={{ minHeight: 44, whiteSpace: "nowrap" }}
        >
          Ver ranking completo
        </Button>
      }
      sx={{ mt: 3, alignItems: "center" }}
    >
      Estás viendo las primeras {visibleCount} posiciones. Necesitas una licencia
      compatible para consultar el ranking completo de {feature.title.toLowerCase()},
      buscar, ordenar y aplicar filtros avanzados.
    </Alert>
  );
}

export function LockedAction({
  permission,
  children,
  disabled,
  onClick,
  ...buttonProps
}) {
  const { hasPermission, permissionError, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { requestUpgrade } = useLicenseUpgrade();

  if (hasPermission(permission)) {
    return (
      <Button {...buttonProps} disabled={disabled} onClick={onClick}>
        {children}
      </Button>
    );
  }

  return (
    <Button
      {...buttonProps}
      disabled={Boolean(permissionError || loading)}
      startIcon={<LockOutlined />}
      onClick={() => {
        if (requestUpgrade) {
          requestUpgrade(permission);
          return;
        }
        const returnTo = `${location.pathname}${location.search}`;
        rememberReturnPosition(returnTo);
        navigate(
          `/licencias?required=${encodeURIComponent(permission)}&returnTo=${encodeURIComponent(returnTo)}`
        );
      }}
    >
      {children}
    </Button>
  );
}
